import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { APIError, useAxios } from '../../core/_axios';
import { SelectableObituary, Service } from '../../core/_models';
import { ActiveServiceBanner } from '../../components/ActiveServiceBanner';
import { useNavigate } from 'react-router-dom';
import ModalDeceasedPersonPhotoCropper from '../../components/cropper/ModalDeceasedPersonPhotoCropper';
import LoadingSpinner from '../../components/LoadingSpinner';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';
import { createService, getObituariesThatCanBeUsedToCreateService, updateServiceByUniqueIdentifier } from '../../core/_requests_v2';
import { useAuth } from '../../AuthContext';
import useAccount from '../../hooks/useAccount';
import { isPaymentOutOfDate, getAgencyName } from '../../utils/paymentUtils';

const GridOfSelectableObituaries = ({
    obituaries,
    onSelectedObituary
}: {
    obituaries: SelectableObituary[],
    onSelectedObituary: (obituary: SelectableObituary) => void
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (obituaries.length === 0) {
        return <p>No obituaries available.</p>;
    }

    // Only show the first 4 obituaries if not expanded
    const visibleObituaries = isExpanded ? obituaries : obituaries.slice(0, 4);

    return (
        <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {visibleObituaries.map((obituary) => {
                    const profileImageUrl = obituary.public_data.latest_obituary_detail_extraction.result.avatar_url;

                    return (
                        <div
                            key={obituary.unique_identifier}
                            className="flex flex-col items-center cursor-pointer"
                            onClick={() => {
                                onSelectedObituary(obituary)
                                setIsExpanded(false)
                            }}
                        >
                            {profileImageUrl ? (
                                <img
                                    src={profileImageUrl}
                                    alt={obituary.public_data.latest_obituary_detail_extraction.result.full_name}
                                    className="w-16 h-16 object-cover rounded-full"
                                />
                            ) : (
                                <div className="w-16 h-16 bg-gray-300 rounded-full" />
                            )}
                            <p className="text-center mt-2 text-xs font-medium">
                                {obituary.public_data.latest_obituary_detail_extraction.result.full_name}
                            </p>
                        </div>
                    );
                })}
            </div>

            {/* Show "Show more" link only if there are more than 4 obituaries */}
            {obituaries.length > 4 && (
                <div className="mt-4 text-center">
                    <button
                        className="text-blue-500 hover:underline"
                        onClick={() => setIsExpanded(!isExpanded)}
                    >
                        {isExpanded ? 'Show less' : 'Show more'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default function ManageServiceForm({
    service = undefined,
    onSetService = undefined
}: {
    service?: Service,
    onSetService?: (service: Service) => void
}) {
    const { copy } = useLocalizedCopy();

    const axios = useAxios();

    const navigate = useNavigate();

    const { currentUser, viewingAllAccounts } = useAuth();
    const { account } = useAccount({
        axios,
        isForCurrentAccount: !viewingAllAccounts
    });

    const [savingService, setSavingService] = useState(false);
    const [errorSavingService, setErrorSavingService] = useState<string | null>(null);
    const [successSavingService, setSuccessSavingService] = useState<string | null>(null);

    const [photoOfPersonBlob, setPhotoOfPersonBlob] = useState<Blob | null>(null)

    const [loadingObituaries, setLoadingObituaries] = useState(service ? false : true);
    const [loadingObituariesError, setLoadingObituariesError] = useState<string | null>(null);
    const [obituariesThatCanBeUsed, setObituariesThatCanBeUsed] = useState<SelectableObituary[]>([]);
    const [totalObituariesThatCanBeUsed, setTotalObituariesThatCanBeUsed] = useState<null | number>(null);

    const [selectedObituary, setSelectedObituary] = useState<SelectableObituary | null>(null);

    const [deceasedPersonsFullName, setDeceasedPersonsFullName] = useState<string>(service?.public_data.deceased_person.full_name || '');
    const [deceasedPersonsPreferredName, setDeceasedPersonsPreferredName] = useState<string>(service?.public_data.deceased_person.preferred_name || '');
    const [deceasedPersonsBirthDate, setDeceasedPersonsBirthDate] = useState<string>(service?.public_data.deceased_person.birth_date || '');
    const [deceasedPersonsDeathDate, setDeceasedPersonsDeathDate] = useState<string>(service?.public_data.deceased_person.death_date || '');

    const [advancedPlanningCheckText, setAdvancedPlanningCheckText] = useState<string | undefined | null>(service?.public_data.advanced_planning_check_text);
    const [advancedPlanningCheckIsHidden, setAdvancedPlanningCheckIsHidden] = useState<boolean | undefined | null>(service?.public_data.advanced_planning_check_is_hidden);
    const [advancedPlanningCheckDefaultChecked, setAdvancedPlanningCheckDefaultChecked] = useState<boolean | undefined | null>(service?.public_data.advanced_planning_check_default_checked);
    const [mobileAdvancedPlanningLabelHeader, setMobileAdvancedPlanningLabelHeader] = useState<string | undefined | null>(service?.public_data.mobile_advanced_planning_label_header);
    const [mobileAdvancedPlanningQuestion, setMobileAdvancedPlanningQuestion] = useState<string | undefined | null>(service?.public_data.mobile_advanced_planning_question);

    const [viewingSampleAvatars, setViewingSampleAvatars] = useState(false);
    const [selectedSampleAvatarUrl, setSelectedSampleAvatarUrl] = useState<string | null>(null);

    // Initialize viewAdvancedOptions from localStorage or based on existing data
    const [viewAdvancedOptions, setViewAdvancedOptions] = useState(() => {
        // Check localStorage first
        const savedState = localStorage.getItem('viewAdvancedOptions');
        if (savedState !== null) {
            return savedState === 'true';
        }
        // Fall back to checking if there's existing data
        return typeof advancedPlanningCheckText === 'string' || 
               typeof advancedPlanningCheckIsHidden === 'boolean' || 
               typeof advancedPlanningCheckDefaultChecked === 'boolean';
    });

    const sampleAvatarUrls = useMemo(() => {
        return [
            'https://upload.serviceguestbook.com/design/placeholder-avatar-feather-on-stone.png',
            'https://upload.serviceguestbook.com/design/placeholder-avatar-doves.png',
            'https://upload.serviceguestbook.com/design/placeholder-avatar-candle.png',
        ]
    }, []);

    // Save viewAdvancedOptions state to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('viewAdvancedOptions', viewAdvancedOptions.toString());
    }, [viewAdvancedOptions]);

    const onStartServiceFormSubmitted = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (savingService) {
            return;
        }
        if (!service && !photoOfPersonBlob) {
            setErrorSavingService('Please upload a photo of the person.')
            return
        }
        setSuccessSavingService(null);
        setErrorSavingService(null);
        setSavingService(true);
        const data = {
            deceased_person_full_name: deceasedPersonsFullName,
            deceased_person_preferred_name: deceasedPersonsPreferredName,
            deceased_person_birth_date: deceasedPersonsBirthDate,
            deceased_person_death_date: deceasedPersonsDeathDate,
            ...(typeof advancedPlanningCheckText === 'string' && { advanced_planning_check_text: advancedPlanningCheckText }),
            ...(typeof advancedPlanningCheckIsHidden === 'boolean' && { advanced_planning_check_is_hidden: advancedPlanningCheckIsHidden }),
            ...(typeof advancedPlanningCheckDefaultChecked === 'boolean' && { advanced_planning_check_default_checked: advancedPlanningCheckDefaultChecked }),
            ...(typeof mobileAdvancedPlanningLabelHeader === 'string' && { mobile_advanced_planning_label_header: mobileAdvancedPlanningLabelHeader }),
            ...(typeof mobileAdvancedPlanningQuestion === 'string' && { mobile_advanced_planning_question: mobileAdvancedPlanningQuestion }),
        }
        console.log('Sending data:', data);
        console.log('advancedPlanningCheckDefaultChecked:', advancedPlanningCheckDefaultChecked, 'type:', typeof advancedPlanningCheckDefaultChecked);
        if (!service) {
            try {
                const resultingService = await createService(axios, {
                    ...data,
                }, photoOfPersonBlob)
                navigate(`/services/${resultingService.unique_identifier}/send-service-to-ipad`)
            } catch (exception) {
                console.error(exception)
                const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
                setErrorSavingService(errorMessage)
                setSavingService(false)
            }
        } else {
            try {
                const resultingService = await updateServiceByUniqueIdentifier(axios, service.unique_identifier, {
                    ...data,
                }, photoOfPersonBlob)
                if (!onSetService) {
                    throw new Error('onSetService is required when updating a service')
                }
                setSuccessSavingService('Service updated successfully')
                onSetService(resultingService)
            } catch (exception) {
                console.error(exception)
                const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
                setErrorSavingService(errorMessage)
            } finally {
                setSavingService(false)
            }
        }
    }, [
        axios, navigate, onSetService, photoOfPersonBlob, savingService, service,
        deceasedPersonsFullName, deceasedPersonsPreferredName, deceasedPersonsBirthDate, deceasedPersonsDeathDate,
        advancedPlanningCheckText, advancedPlanningCheckIsHidden, advancedPlanningCheckDefaultChecked,
        mobileAdvancedPlanningLabelHeader, mobileAdvancedPlanningQuestion
    ]);

    const loadObituariesThatCanBeUsed = useCallback(async () => {
        // DISABLED: Obituary quick-select functionality temporarily disabled
        // Will be re-implemented in the future
        setLoadingObituaries(false);
        setObituariesThatCanBeUsed([]);
        setTotalObituariesThatCanBeUsed(0);
        return;
        
        /* Original implementation - keeping for reference
        if (service) {
            // Obituaries are only used to quickly load data into the form
            setLoadingObituaries(false);
            return;
        }
        setLoadingObituaries(true);
        setLoadingObituariesError(null);
        try {
            const {
                obituaries: updatedObituaries,
                obituaries_count: updatedObituariesCount
            } = await getObituariesThatCanBeUsedToCreateService(axios);
            setObituariesThatCanBeUsed(updatedObituaries);
            setTotalObituariesThatCanBeUsed(updatedObituariesCount);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setLoadingObituariesError(errorMessage)
        } finally {
            setLoadingObituaries(false)
        }
        */
    }, [axios, service]);

    useEffect(() => {
        loadObituariesThatCanBeUsed();
    }, [loadObituariesThatCanBeUsed]);

    if (loadingObituaries) {
        return (
            <div className="container-fixed">
                <LoadingSpinner />
            </div>
        );
    }

    return <>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
            <div className="col-span-2">
                {loadingObituariesError && <div className="mb-5">
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                        <span className="block sm:inline">
                            Failed to load obituaries: {loadingObituariesError}
                        </span>
                    </div>
                </div>}
                <div className="w-full">
                    <ActiveServiceBanner
                        viewingService={service}
                    />
                </div>
                {selectedObituary ? <>
                    <div className="card p-4 mb-5">
                        <h3 className="font-medium text-gray-900 dark:text-white">
                            Using: {selectedObituary.public_data.latest_obituary_detail_extraction.result.full_name}
                        </h3>
                        <div className="mb-2">
                            Loaded from: <a href={selectedObituary.url} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">
                                {selectedObituary.url}
                            </a>
                        </div>
                        <div>
                            <button className="btn btn-sm btn-light" onClick={() => {
                                setSelectedObituary(null)
                                setDeceasedPersonsFullName('')
                                setDeceasedPersonsPreferredName('')
                                setDeceasedPersonsBirthDate('')
                                setDeceasedPersonsDeathDate('')
                                setPhotoOfPersonBlob(null)
                            }}>
                                Clear
                            </button>
                        </div>
                    </div>
                </> : <>
                    {/* DISABLED: Obituary quick-select functionality temporarily disabled */}
                    {/* Will be re-implemented in the future */}
                    {false && totalObituariesThatCanBeUsed > 0 && (
                        <div className="card p-4 mb-5">
                            <div>
                                <h3 className="font-medium text-gray-900 dark:text-white mb-5">
                                    {`Select an obituary to quickly start a service:`}
                                </h3>
                            </div>
                            <GridOfSelectableObituaries
                                obituaries={obituariesThatCanBeUsed}
                                onSelectedObituary={(obituary) => {
                                    setSelectedObituary(obituary);
                                    setDeceasedPersonsFullName(obituary.public_data.latest_obituary_detail_extraction.result.full_name);
                                    setDeceasedPersonsPreferredName(obituary.public_data.latest_obituary_detail_extraction.result.preferred_name || '');
                                    setDeceasedPersonsBirthDate(obituary.public_data.latest_obituary_detail_extraction.result.date_of_birth || '');
                                    setDeceasedPersonsDeathDate(obituary.public_data.latest_obituary_detail_extraction.result.date_of_death || '');
                                    // set setPhotoOfPersonBlob based on the avatar URL
                                    const avatarUrl = obituary.public_data.latest_obituary_detail_extraction.result.avatar_url;
                                    if (avatarUrl) {
                                        fetch(avatarUrl)
                                            .then((response) => response.blob())
                                            .then((blob) => setPhotoOfPersonBlob(blob))
                                            .catch((error) => console.error(error));
                                    }
                                }}
                            />
                        </div>
                    )}
                </>}
                <div className="flex flex-col gap-5 lg:gap-7.5">
                    <form className="card" onSubmit={onStartServiceFormSubmitted}>
                        <div className="card-header">
                            <h3 className="card-title">
                                {copy.CreateService.subtitle}
                            </h3>
                        </div>
                        <div className="card-body grid gap-3.5 py-3 2xl:py-7.5">
                            {false && <>
                                {service && <>
                                    {service.public_data.ipad_templates && <>
                                        <div>
                                            <img src={service.public_data.ipad_templates.portrait_url} alt="Portrait template" className="w-full h-auto" />
                                        </div>
                                    </>}
                                </>}
                            </>}
                            <div className="flex flex-col gap-4">
                                <div>
                                    <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                        {copy.CreateService.form.photoLabel}
                                    </label>
                                    <ModalDeceasedPersonPhotoCropper
                                        setPhotoBlob={setPhotoOfPersonBlob}
                                        currentPhotoUrl={
                                            selectedObituary?.public_data.latest_obituary_detail_extraction?.result.avatar_url || service?.public_data.deceased_person.avatar_url || selectedSampleAvatarUrl || ''
                                        }
                                    />
                                </div>
                            </div>
                            {!service && !selectedSampleAvatarUrl && <>
                                {!viewingSampleAvatars ? <>
                                    <div className="flex flex-col gap-4">
                                        {/* Small link, like edit */}
                                        <span
                                            className="text-blue-500 hover:underline cursor-pointer"
                                            onClick={() => setViewingSampleAvatars(true)}
                                        >
                                            {copy.CreateService.form.useSampleAvatarLink}
                                        </span>
                                    </div>
                                </> : <div className="flex flex-col gap-4">
                                    <div>
                                        <label
                                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                            {copy.CreateService.form.selectSampleAvatarLabel}
                                        </label>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
                                        {sampleAvatarUrls.map((avatarUrl) => (
                                            <div
                                                key={avatarUrl}
                                                className="flex flex-col items-center cursor-pointer"
                                                onClick={() => {
                                                    setSelectedSampleAvatarUrl(avatarUrl)
                                                    fetch(avatarUrl)
                                                        .then((response) => response.blob())
                                                        .then((blob) => setPhotoOfPersonBlob(blob))
                                                        .catch((error) => console.error(error));
                                                }}
                                            >
                                                <img
                                                    src={avatarUrl}
                                                    alt="Sample avatar"
                                                    className="w-16 h-16 object-cover rounded-full"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                }
                            </>}
                            <div className="flex flex-col gap-4">
                                <div>
                                    <label
                                        htmlFor="deceased_person_full_name"
                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                        {copy.CreateService.form.fullNameLabel}
                                    </label>
                                    <input
                                        id="deceased_person_full_name"
                                        name="deceased_person_full_name"
                                        type="text"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                        placeholder={copy.CreateService.form.fullNamePlaceholder}
                                        required
                                        autoComplete='off'
                                        autoFocus={!service}
                                        value={deceasedPersonsFullName}
                                        onChange={(event) => setDeceasedPersonsFullName(event.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="flex flex-col gap-4">
                                <div>
                                    <label
                                        htmlFor="deceased_person_preferred_name"
                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                        {copy.CreateService.form.preferredNameLabel}
                                    </label>
                                    <input
                                        id="deceased_person_preferred_name"
                                        name="deceased_person_preferred_name"
                                        type="text"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                        placeholder={copy.CreateService.form.preferredNamePlaceholder}
                                        required={false}
                                        autoComplete='off'
                                        value={deceasedPersonsPreferredName}
                                        onChange={(event) => setDeceasedPersonsPreferredName(event.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="grid gap-6 mb-0 md:grid-cols-2">
                                <div>
                                    <label htmlFor="deceased_person_birth_date" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                        {copy.CreateService.form.birthDateLabel}
                                    </label>
                                    <input
                                        type="date"
                                        id="deceased_person_birth_date"
                                        name="deceased_person_birth_date"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                        placeholder="MM/DD/YYYY"
                                        required={false}
                                        value={deceasedPersonsBirthDate}
                                        onChange={(event) => setDeceasedPersonsBirthDate(event.target.value)}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="deceased_person_death_date" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                        {copy.CreateService.form.deathDateLabel}
                                    </label>
                                    <input
                                        type="date"
                                        id="deceased_person_death_date"
                                        name="deceased_person_death_date"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                        placeholder="MM/DD/YYYY"
                                        required={false}
                                        value={deceasedPersonsDeathDate}
                                        onChange={(event) => setDeceasedPersonsDeathDate(event.target.value)}
                                    />
                                </div>
                            </div>
                            {/* Service Language Display */}
                            {service && (
                                <div className="flex flex-col gap-2 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                    <label className="block text-sm font-medium text-gray-900 dark:text-white">
                                        {service.public_data.current_language === 'es-ES' ? 'Idioma del Servicio' : 'Service Language'}
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                            service.public_data.current_language === 'es-ES' 
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                                        }`}>
                                            {service.public_data.current_language === 'es-ES' ? copy.Common.labels.languageSpanish : copy.Common.labels.languageEnglish}
                                        </span>
                                        <span className="text-sm text-gray-600 dark:text-gray-400">
                                            {service.public_data.current_language === 'es-ES' 
                                                ? copy.Common.labels.languageTemplateSpanish
                                                : copy.Common.labels.languageTemplateEnglish}
                                        </span>
                                    </div>
                                </div>
                            )}
                            <div className={`flex items-center ${viewAdvancedOptions ? '' : 'hidden'}`}>
                                <input
                                    id="advanced_planning_check_is_hidden"
                                    name="advanced_planning_check_is_hidden"
                                    type="checkbox"
                                    className="mr-2"
                                    checked={advancedPlanningCheckIsHidden}
                                    onChange={() => setAdvancedPlanningCheckIsHidden(!advancedPlanningCheckIsHidden)}
                                />
                                <label htmlFor="advanced_planning_check_is_hidden" className="text-sm font-medium text-gray-900 dark:text-white">
                                    {copy.CreateService.form.hidePreplanningCheckboxLabel}
                                </label>
                            </div>
                            <div className={`flex items-center ${(viewAdvancedOptions && !advancedPlanningCheckIsHidden) ? '' : 'hidden'}`}>
                                <input
                                    id="advanced_planning_check_default_checked"
                                    name="advanced_planning_check_default_checked"
                                    type="checkbox"
                                    className="mr-2"
                                    checked={advancedPlanningCheckDefaultChecked || false}
                                    onChange={() => setAdvancedPlanningCheckDefaultChecked(!advancedPlanningCheckDefaultChecked)}
                                />
                                <label htmlFor="advanced_planning_check_default_checked" className="text-sm font-medium text-gray-900 dark:text-white">
                                    {copy.CreateService.form.defaultPreplanningCheckboxLabel}
                                </label>
                            </div>
                            <div className={`flex flex-col gap-4 ${(viewAdvancedOptions && !advancedPlanningCheckIsHidden) ? '' : 'hidden'}`}>
                                <div>
                                    <label
                                        htmlFor="advanced_planning_check_text"
                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                        {copy.CreateService.form.preplanningLabel}
                                    </label>
                                    <input
                                        id="advanced_planning_check_text"
                                        name="advanced_planning_check_text"
                                        type="text"
                                        className={`bg-gray-50 border ${advancedPlanningCheckIsHidden ? 'border-gray-300 text-gray-400' : 'border-gray-300 text-gray-900'} text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500`}
                                        placeholder={copy.CreateService.form.preplanningLabelPlaceholder}
                                        value={advancedPlanningCheckText || ''}
                                        onChange={(event) => setAdvancedPlanningCheckText(event.target.value)}
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="mobile_advanced_planning_label_header"
                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                        {copy.CreateService.form.mobileLabelHeaderLabel}
                                    </label>
                                    <input
                                        id="mobile_advanced_planning_label_header"
                                        name="mobile_advanced_planning_label_header"
                                        type="text"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                        placeholder={copy.CreateService.form.mobileLabelHeaderPlaceholder}
                                        value={mobileAdvancedPlanningLabelHeader || ''}
                                        onChange={(event) => setMobileAdvancedPlanningLabelHeader(event.target.value)}
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="mobile_advanced_planning_question"
                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                        {copy.CreateService.form.mobileQuestionLabel}
                                    </label>
                                    <input
                                        id="mobile_advanced_planning_question"
                                        name="mobile_advanced_planning_question"
                                        type="text"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                        placeholder={copy.CreateService.form.mobileQuestionPlaceholder}
                                        value={mobileAdvancedPlanningQuestion || ''}
                                        onChange={(event) => setMobileAdvancedPlanningQuestion(event.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="card-footer py-3.5 flex flex-col lg:flex-row items-center justify-between">
                            {(savingService || errorSavingService || successSavingService) ? <div className="flex flex-col space-y-2">
                                {savingService && (
                                    <div className="text-sm font-bold">
                                        {service ? copy.CreateService.form.savingUpdateMsg : copy.CreateService.form.savingCreateMsg}
                                    </div>
                                )}
                                {errorSavingService && <div className="text-red-500 text-sm font-medium">{errorSavingService}</div>}
                                {successSavingService && <div className="text-green-500 text-sm font-medium">{successSavingService}</div>}
                            </div> :
                                <div className="flex items-center">
                                    <input
                                        id="view_advanced_options"
                                        type="checkbox"
                                        className="mr-2"
                                        checked={viewAdvancedOptions}
                                        onChange={() => {
                                            const newValue = !viewAdvancedOptions;
                                            setViewAdvancedOptions(newValue);
                                        }}
                                    />
                                    <label htmlFor="view_advanced_options" className="text-sm font-medium text-gray-900 dark:text-white">
                                        {copy.CreateService.form.viewAdvancedOptionsLabel}
                                    </label>
                                </div>}

                            {/* Payment gate warning for service updates */}
                            {service && isPaymentOutOfDate(currentUser, account) && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                                    <div className="flex items-start gap-3">
                                        <i className="ki-filled ki-information-circle text-red-600 text-xl mt-0.5"></i>
                                        <div>
                                            <h4 className="font-semibold text-red-900 mb-1">
                                                Updates Restricted
                                            </h4>
                                            <p className="text-sm text-red-700">
                                                Service updates are currently restricted because payment for {getAgencyName(currentUser, account)} is past due. Please contact your agency administrator or SGB support to resolve this issue.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div>
                                {service ? (
                                    <button
                                        type="submit"
                                        className="btn btn-sm btn-primary"
                                        disabled={savingService || isPaymentOutOfDate(currentUser, account)}
                                    >
                                        {!savingService ? copy.CreateService.form.updateButton : copy.CreateService.form.savingUpdateMsg}
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        className="btn btn-sm btn-primary"
                                        disabled={savingService}
                                    >
                                        {!savingService ? copy.CreateService.form.createButton : copy.CreateService.form.savingCreateMsg}
                                    </button>
                                )}
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </>
}